import { Navigate, Outlet, Route, Routes, useLocation } from "react-router-dom";
import { errorMessage } from "./api/client";
import { OrgProvider, useMe, useOrg } from "./api/org";
import { useAuth } from "./auth/auth";
import { AdminLayout, OrgLayout } from "./components/Layout";
import { Alert, Button, Spinner } from "./components/ui";
import { AuthCallback, Login } from "./pages/Login";
import { Onboarding } from "./pages/Onboarding";
import { GetStarted } from "./pages/GetStarted";
import { Designs } from "./pages/Designs";
import { DesignDetail } from "./pages/DesignDetail";
import { Widget } from "./pages/Widget";
import { Branding } from "./pages/Branding";
import { Rules } from "./pages/Rules";
import { Credits } from "./pages/Credits";
import { Team } from "./pages/Team";
import { AdminOrgs } from "./pages/admin/Orgs";
import { AdminOrgDetail } from "./pages/admin/OrgDetail";
import { AdminUsers } from "./pages/admin/Users";
import { AdminTrials } from "./pages/admin/Trials";
import { AdminPlatformSettings } from "./pages/admin/PlatformSettings";
import { AdminStages } from "./pages/admin/Stages";
import { AdminCache } from "./pages/admin/Cache";

function SignedIn() {
  const auth = useAuth();
  const location = useLocation();
  const me = useMe();
  if (!auth.ready) return <Spinner />;
  if (!auth.signedIn) return <Navigate to="/login" replace state={{ returnTo: location.pathname + location.search }} />;
  if (me.isLoading) return <Spinner label="Signing in…" />;
  if (me.error || !me.data)
    return (
      <div className="center-fill" style={{ minHeight: "100vh", padding: 16 }}>
        <div style={{ maxWidth: 460 }} className="stack">
          <Alert tone="danger" title="Could not load your account.">
            {errorMessage(me.error)}
          </Alert>
          <div className="row">
            <Button onClick={() => me.refetch()}>Try again</Button>
            <Button variant="ghost" onClick={() => auth.signOut()}>
              Sign out
            </Button>
          </div>
        </div>
      </div>
    );
  return (
    <OrgProvider me={me.data}>
      <Outlet />
    </OrgProvider>
  );
}

function RequireOrg() {
  const { orgId } = useOrg();
  if (!orgId) return <Navigate to="/onboarding" replace />;
  return <Outlet />;
}

function RequireAdmin() {
  const { isPlatformAdmin } = useOrg();
  if (!isPlatformAdmin) return <Navigate to="/" replace />;
  return <Outlet />;
}

function BillingRedirect() {
  const { search } = useLocation();
  return <Navigate to={`/credits${search}`} replace />;
}

function Home() {
  const { orgId, isPlatformAdmin } = useOrg();
  if (!orgId) return <Navigate to={isPlatformAdmin ? "/admin/orgs" : "/onboarding"} replace />;
  return <Navigate to="/designs" replace />;
}

export function App() {
  return (
    <Routes>
      <Route path="/login" element={<Login />} />
      <Route path="/signup" element={<Login signup />} />
      <Route path="/auth/callback" element={<AuthCallback />} />
      <Route element={<SignedIn />}>
        <Route index element={<Home />} />
        <Route path="/onboarding" element={<Onboarding />} />
        <Route element={<RequireOrg />}>
          <Route element={<OrgLayout />}>
            <Route path="/get-started" element={<GetStarted />} />
            <Route path="/designs" element={<Designs />} />
            <Route path="/designs/:designId" element={<DesignDetail />} />
            <Route path="/widget" element={<Widget />} />
            <Route path="/branding" element={<Branding />} />
            <Route path="/rules" element={<Rules />} />
            <Route path="/credits" element={<Credits />} />
            {/* The backend's default checkout return address (and the mock provider's). */}
            <Route path="/billing" element={<BillingRedirect />} />
            <Route path="/team" element={<Team />} />
          </Route>
        </Route>
        <Route element={<RequireAdmin />}>
          <Route element={<AdminLayout />}>
            <Route path="/admin" element={<Navigate to="/admin/orgs" replace />} />
            <Route path="/admin/orgs" element={<AdminOrgs />} />
            <Route path="/admin/orgs/:orgId" element={<AdminOrgDetail />} />
            <Route path="/admin/users" element={<AdminUsers />} />
            <Route path="/admin/trials" element={<AdminTrials />} />
            <Route path="/admin/settings" element={<AdminPlatformSettings />} />
            <Route path="/admin/stages" element={<AdminStages />} />
            <Route path="/admin/cache" element={<AdminCache />} />
          </Route>
        </Route>
      </Route>
      <Route path="*" element={<Navigate to="/" replace />} />
    </Routes>
  );
}
